import React, { useState, useEffect, useRef } from 'react';
import { Share2, X, Copy, Check, Send } from 'lucide-react';
import { t, buildShareText } from '../utils/i18n';
import './ShareMenu.css';

const SHARE_URL = 'https://majesticjukeboxradio.netlify.app';

/**
 * ShareMenu — popover with multiple social options:
 *  - Native share (Web Share API) if available
 *  - X / Twitter
 *  - Facebook
 *  - WhatsApp
 *  - Instagram (copy text + open IG)
 *  - TikTok (copy text + open TikTok)
 *  - Copy link
 *
 * The share text adapts to the user's OS/browser language.
 */
const ShareMenu = ({ track, trigger, anchor = 'right' }) => {
  const [open, setOpen] = useState(false);
  const [copyState, setCopyState] = useState(null); // null | 'link' | 'text'
  const [toast, setToast] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    // Defer attachment to next tick so the click that opened the menu
    // doesn't immediately close it.
    const id = setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [open]);

  if (!track || !track.title || track.title === 'Live') return null;

  const text = buildShareText(track);
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(SHARE_URL);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  };

  const handleNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: track.title, text, url: SHARE_URL });
      } catch (_) {}
      setOpen(false);
    }
  };

  const openWindow = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=640,height=540');
    setOpen(false);
  };

  const handleX = () => {
    openWindow(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`);
  };

  const handleFacebook = () => {
    openWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`);
  };

  const handleWhatsApp = () => {
    openWindow(`https://wa.me/?text=${encodeURIComponent(text + ' ' + SHARE_URL)}`);
  };

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (_) {
      try {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        return true;
      } catch (e) {
        return false;
      }
    }
  };

  const handleInstagram = async () => {
    const ok = await copyToClipboard(`${text} ${SHARE_URL}`);
    if (ok) showToast(t('shareInstaHint'));
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const handleTikTok = async () => {
    const ok = await copyToClipboard(`${text} ${SHARE_URL}`);
    if (ok) showToast(t('shareTiktokHint'));
    window.open('https://www.tiktok.com/', '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const handleCopyLink = async () => {
    const ok = await copyToClipboard(SHARE_URL);
    if (ok) {
      setCopyState('link');
      setTimeout(() => setCopyState(null), 1800);
    }
  };

  return (
    <div className={`share-menu-root anchor-${anchor}`} ref={ref}>
      {trigger ? (
        React.cloneElement(trigger, {
          onClick: (e) => {
            e.stopPropagation();
            if (trigger.props && typeof trigger.props.onClick === 'function') {
              trigger.props.onClick(e);
            }
            setOpen((v) => !v);
          },
        })
      ) : (
        <button
          className="share-trigger-default"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          aria-label={t('shareTitle')}
          title={t('shareTitle')}
          data-testid="share-trigger-button"
        >
          <Share2 size={18} />
        </button>
      )}

      {open && (
        <div className="share-popover" role="menu" data-testid="share-popover">
          <div className="share-popover-header">
            <span>{t('shareTitle')}</span>
            <button
              className="share-popover-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="share-options">
            {typeof navigator !== 'undefined' && navigator.share && (
              <button
                className="share-opt"
                onClick={handleNative}
                data-testid="share-opt-native"
              >
                <span className="share-opt-icon native"><Send size={18} /></span>
                <span>{t('shareNative')}</span>
              </button>
            )}

            <button className="share-opt" onClick={handleX} data-testid="share-opt-x">
              <span className="share-opt-icon x">
                {/* X / Twitter glyph */}
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2H21l-6.49 7.42L22 22h-6.844l-4.79-6.27L4.8 22H2.043l6.96-7.957L2 2h6.91l4.34 5.74L18.244 2zm-2.4 18h1.74L7.24 4H5.4l10.444 16z"/>
                </svg>
              </span>
              <span>X</span>
            </button>

            <button className="share-opt" onClick={handleFacebook} data-testid="share-opt-facebook">
              <span className="share-opt-icon facebook">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M22 12.07C22 6.48 17.52 2 12 2S2 6.48 2 12.07c0 4.99 3.66 9.13 8.44 9.93v-7.02H7.9v-2.91h2.54V9.86c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.91h-2.34V22c4.78-.8 8.44-4.94 8.44-9.93z"/>
                </svg>
              </span>
              <span>Facebook</span>
            </button>

            <button className="share-opt" onClick={handleInstagram} data-testid="share-opt-instagram">
              <span className="share-opt-icon instagram">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
              </span>
              <span>Instagram</span>
            </button>

            <button className="share-opt" onClick={handleTikTok} data-testid="share-opt-tiktok">
              <span className="share-opt-icon tiktok">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M16.5 2h-3v13.4a2.6 2.6 0 1 1-2.6-2.6c.27 0 .53.04.78.12V9.86a5.7 5.7 0 0 0-.78-.06 5.7 5.7 0 1 0 5.7 5.7V8.91a7.6 7.6 0 0 0 4.4 1.4V7.2a4.61 4.61 0 0 1-4.5-5.2z"/>
                </svg>
              </span>
              <span>TikTok</span>
            </button>

            <button className="share-opt" onClick={handleWhatsApp} data-testid="share-opt-whatsapp">
              <span className="share-opt-icon whatsapp">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M20.52 3.48A11.93 11.93 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 5.99L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52zM12 21.82c-1.86 0-3.69-.5-5.29-1.45l-.38-.22-3.67.96.98-3.58-.25-.37A9.79 9.79 0 0 1 2.18 12C2.18 6.59 6.59 2.18 12 2.18S21.82 6.59 21.82 12 17.41 21.82 12 21.82zm5.36-7.32c-.29-.15-1.72-.85-1.99-.95-.27-.1-.46-.15-.66.15-.19.29-.75.95-.93 1.14-.17.19-.34.22-.63.07-.29-.15-1.21-.45-2.31-1.42-.85-.76-1.43-1.69-1.6-1.98-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.15-.17.19-.29.29-.49.1-.19.05-.36-.02-.51-.07-.15-.66-1.6-.91-2.18-.24-.57-.49-.5-.66-.51l-.56-.01c-.19 0-.51.07-.78.36-.27.29-1.02.99-1.02 2.42 0 1.43 1.04 2.81 1.18 3 .15.19 2.05 3.13 4.97 4.39.7.3 1.24.48 1.66.61.7.22 1.34.19 1.84.12.56-.08 1.72-.7 1.96-1.38.24-.68.24-1.27.17-1.38-.07-.12-.27-.19-.56-.34z"/>
                </svg>
              </span>
              <span>WhatsApp</span>
            </button>

            <button className="share-opt" onClick={handleCopyLink} data-testid="share-opt-copy">
              <span className="share-opt-icon copy">
                {copyState === 'link' ? <Check size={18} /> : <Copy size={18} />}
              </span>
              <span>{copyState === 'link' ? t('shareCopied') : t('shareCopy')}</span>
            </button>
          </div>
        </div>
      )}

      {toast && <div className="share-toast" data-testid="share-toast">{toast}</div>}
    </div>
  );
};

export default ShareMenu;
